import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge, PipelineProgress, STAGES, STAGE_LABELS } from "../../components/shared";
import { toast } from "sonner";
import { Download, Package, ExternalLink, Trash2, Headphones, Upload, CheckCircle2, Pencil } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export default function ProjectDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "super_admin";

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [payState, setPayState] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", internal_note: "", author_comment: "" });
  const [kdp, setKdp] = useState({ asin: "", amazon_url: "" });
  const [splits, setSplits] = useState([]);
  const [copyQty, setCopyQty] = useState(1);
  const [copyShip, setCopyShip] = useState({ name: "", email: "", address1: "", address2: "", city: "", state: "", postal_code: "", country: "United States" });
  const [ordering, setOrdering] = useState(false);
  const [modules, setModules] = useState([]);
  const [abBrief, setAbBrief] = useState("");
  const [abForm, setAbForm] = useState({ status: "requested", url: "" });
  const [abBusy, setAbBusy] = useState(false);
  const [appForm, setAppForm] = useState({ kind: "general", message: "" });
  const [appNotes, setAppNotes] = useState({});
  const [appBusy, setAppBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const [planEdit, setPlanEdit] = useState(false);
  const [planForm, setPlanForm] = useState({ plan_id: "", amount_paid: "" });
  const [planOptions, setPlanOptions] = useState([]);
  const [planBusy, setPlanBusy] = useState(false);
  const pollRef = useRef(0);

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data);
      setStatusForm((s) => ({ ...s, status: r.data.status === "draft" ? "submitted" : r.data.status }));
      setKdp({ asin: r.data.asin || "", amazon_url: r.data.amazon_url || "" });
      if (r.data.audiobook) setAbForm({ status: r.data.audiobook.status, url: r.data.audiobook.url || "" });
    }).catch(() => navigate("/dashboard"));
    api.get(`/projects/${id}/files`).then((r) => setFiles(r.data));
    api.get(`/projects/${id}/comments`).then((r) => setComments(r.data));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const openPlanEdit = async (mode) => {
    try {
      let opts = planOptions;
      if (!opts.length) {
        const { data } = await api.get("/plans");
        opts = data;
        setPlanOptions(data);
      }
      setPlanForm({ plan_id: project.plan_id || opts[0]?.id || "", amount_paid: project.amount_paid ?? (mode === "offline" ? opts[0]?.price ?? "" : ""), note: "" });
      setPlanEdit(mode);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const savePlan = async () => {
    setPlanBusy(true);
    try {
      const amount = planForm.amount_paid === "" ? null : parseFloat(planForm.amount_paid);
      if (planEdit === "offline") {
        await api.post(`/admin/projects/${id}/record-payment`, { plan_id: planForm.plan_id, amount, note: planForm.note });
        toast.success("Offline payment recorded — book is now in the production pipeline");
      } else {
        await api.put(`/admin/projects/${id}/plan`, { plan_id: planForm.plan_id, amount_paid: amount });
        toast.success("Submission plan updated — author notified");
      }
      setPlanEdit(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setPlanBusy(false);
  };

  const renameFile = async (f) => {
    const name = window.prompt("New file name", f.filename);
    if (!name || name.trim() === "" || name === f.filename) return;
    try {
      await api.put(`/files/${f.id}`, { filename: name.trim() });
      toast.success("File renamed");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.filename}" (${f.kind} v${f.version})?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("File deleted");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/admin/settings").then((r) => setSplits(r.data.royalty_split_templates || []));
    }
    if (user?.role === "author") {
      api.get("/membership/my").then((r) => setModules(r.data.modules || [])).catch(() => {});
      setCopyShip((s) => ({ ...s, name: s.name || user.name || "", email: s.email || user.email || "" }));
    }
  }, [user]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("checking");
    const poll = async () => {
      if (pollRef.current >= 6) { setPayState("timeout"); return; }
      pollRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          setParams({});
          load();
          return;
        }
        if (data.status === "expired") { setPayState("expired"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [params, setParams, load]);

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/projects/${id}/comments`, { text: newComment });
      setNewComment("");
      api.get(`/projects/${id}/comments`).then((r) => setComments(r.data));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const updateStatus = async () => {
    try {
      await api.put(`/admin/projects/${id}/status`, statusForm);
      toast.success("Status updated — author notified");
      setStatusForm((s) => ({ ...s, internal_note: "", author_comment: "" }));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveKdp = async () => {
    try {
      await api.put(`/admin/projects/${id}/kdp`, kdp);
      toast.success("Listing info saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const requestAudiobook = async () => {
    setAbBusy(true);
    try {
      await api.post(`/projects/${id}/audiobook-request`, { brief: abBrief });
      toast.success("Audiobook requested — our team will be in touch");
      setAbBrief("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAbBusy(false); }
  };

  const saveAudiobook = async () => {
    setAbBusy(true);
    try {
      await api.put(`/admin/projects/${id}/audiobook`, abForm);
      toast.success("Audiobook updated — author notified");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAbBusy(false); }
  };

  const setSplit = async (template_id) => {
    try {
      await api.put(`/admin/projects/${id}/split`, { template_id });
      toast.success("Royalty split updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const downloadPacket = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${id}/packet`, "_blank");
  };

  const deleteProject = async () => {
    if (!window.confirm(`PERMANENTLY delete "${project.title}"?\n\nThis removes the project, its manuscript/cover files, comments and any sales rows tied to it. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/admin/projects/${id}`);
      toast.success(data.message);
      navigate("/admin");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const orderCopies = async (e) => {
    e.preventDefault();
    setOrdering(true);
    try {
      const { data } = await api.post("/store/author-order", {
        project_id: id, quantity: parseInt(copyQty) || 1, origin_url: window.location.origin, shipping: copyShip });
      window.location.href = data.url;
    } catch (e2) { toast.error(fmtErr(e2)); setOrdering(false); }
  };

  const staffUpload = async (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(kind);
    try {
      await api.post(`/projects/${id}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "cover" ? "Cover" : "Manuscript"} uploaded as a new version`);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const sendApproval = async () => {
    setAppBusy(true);
    try {
      await api.post(`/admin/projects/${id}/approval-request`, appForm);
      toast.success("Approval request sent — the author has been notified");
      setAppForm({ kind: "general", message: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAppBusy(false); }
  };

  const respondApproval = async (a, action) => {
    setAppBusy(true);
    try {
      await api.post(`/projects/${id}/approvals/${a.id}/respond`, { action, note: appNotes[a.id] || "" });
      toast.success(action === "approve" ? "Approved — thank you!" : action === "reject" ? "Rejected — our team has been notified" : "Change request sent to our team");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAppBusy(false); }
  };

  if (!project) return <p className="text-neutral-500">Loading...</p>;

  return (
    <div data-testid="project-detail-page">
      {payState === "checking" && <div className="text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-4 text-sm mb-6" data-testid="payment-checking">Verifying your payment...</div>}
      {payState === "paid" && <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm mb-6" data-testid="payment-success">Payment received — thank you! A receipt was sent to your email.</div>}
      {payState === "expired" && <div className="text-red-700 bg-red-50 border border-red-200 rounded-md p-4 text-sm mb-6">Payment session expired. Please try again.</div>}
      {payState === "timeout" && <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm mb-6">Payment status check timed out. Refresh in a moment or check your email.</div>}

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">{project.title}</h1>
            <StatusBadge status={project.status} />
          </div>
          {project.subtitle && <p className="text-neutral-500 mt-1">{project.subtitle}</p>}
          {isStaff && <p className="text-sm text-neutral-500 mt-1">Author: {project.author_name} ({project.author_email})</p>}
        </div>
        {project.status === "draft" && !isStaff && (
          <Link to={`/projects/new?id=${project.id}`} data-testid="resume-submission-button"
            className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Resume submission</Link>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <PipelineProgress status={project.status} />
        {project.amazon_url && (
          <a href={project.amazon_url} target="_blank" rel="noreferrer" data-testid="amazon-link"
            className="inline-flex items-center gap-1.5 text-sm text-blue-700 font-medium mt-3 hover:underline">
            View on Amazon <ExternalLink size={14} />
          </a>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h3 className="font-semibold font-heading mb-4">Book details</h3>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[["Genre", project.genre], ["Language", project.language],
                ["Keywords", (project.keywords || []).join(", ")], ["Categories", (project.categories || []).join(", ")],
                ["Target price", project.target_price ? `$${project.target_price}` : ""],
                ["ASIN", project.asin], ["Royalty split", project.royalty_split ? `${project.royalty_split.name} (you keep ${project.royalty_split.author_pct}%)` : "—"]].map(([k, v]) => (
                <div key={k}><dt className="text-neutral-400 text-xs uppercase tracking-wider">{k}</dt><dd className="text-neutral-800 mt-0.5">{v || "—"}</dd></div>
              ))}
              <div>
                <dt className="text-neutral-400 text-xs uppercase tracking-wider">Plan</dt>
                <dd className="text-neutral-800 mt-0.5 flex items-center gap-1.5" data-testid="project-plan-value">
                  {project.plan_name ? `${project.plan_name} ($${project.amount_paid})` : "Not paid"}
                  {user?.role === "super_admin" && project.locked && (
                    <button data-testid="edit-submission-plan-button" onClick={() => openPlanEdit("edit")} title="Edit this book's submission plan"
                      className="p-1 text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 rounded">
                      <Pencil size={12} />
                    </button>
                  )}
                  {user?.role === "super_admin" && !project.locked && (
                    <button data-testid="record-offline-payment-button" onClick={() => openPlanEdit("offline")}
                      title="Record a payment made outside Stripe (check, bank transfer...) and put this book in the pipeline"
                      className="ml-1 px-2 py-0.5 border border-emerald-300 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-50">
                      Record offline payment
                    </button>
                  )}
                </dd>
              </div>
            </dl>
            {planEdit && (
              <div data-testid="project-plan-editor" className="mt-4 border-t border-neutral-100 pt-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-600">Submission plan</label>
                    <select data-testid="project-plan-select" value={planForm.plan_id}
                      onChange={(e) => {
                        const p = planOptions.find((x) => x.id === e.target.value);
                        setPlanForm((f) => ({ ...f, plan_id: e.target.value, amount_paid: p?.price ?? "" }));
                      }}
                      className="mt-1 block bg-white border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-neutral-900 outline-none">
                      {planOptions.map((p) => <option key={p.id} value={p.id}>{`${p.name} — $${p.price}`}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600">{planEdit === "offline" ? "Amount received ($)" : "Amount paid ($)"}</label>
                    <input data-testid="project-plan-amount" type="number" step="0.01" min="0" value={planForm.amount_paid}
                      onChange={(e) => setPlanForm((f) => ({ ...f, amount_paid: e.target.value }))}
                      className="mt-1 block w-32 bg-white border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
                  </div>
                  {planEdit === "offline" && (
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-xs font-medium text-neutral-600">Note (optional)</label>
                      <input data-testid="project-plan-note" value={planForm.note} placeholder="e.g. Check #1042, bank transfer..."
                        onChange={(e) => setPlanForm((f) => ({ ...f, note: e.target.value }))}
                        className="mt-1 block w-full bg-white border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
                    </div>
                  )}
                  <button data-testid="project-plan-save" onClick={savePlan} disabled={planBusy || !planForm.plan_id}
                    className="px-4 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                    {planBusy ? "Saving..." : planEdit === "offline" ? "Record payment" : "Save"}
                  </button>
                  <button data-testid="project-plan-cancel" onClick={() => setPlanEdit(false)}
                    className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
                </div>
                <p className="text-xs text-neutral-400 mt-2">
                  {planEdit === "offline"
                    ? "Marks this submission as paid (outside Stripe), locks it into the production pipeline and starts the author's 30-day membership."
                    : "Updates this book's submission plan & recorded amount only — the author's monthly membership is managed from Users → Plan."}
                </p>
              </div>
            )}
            {project.description && <p className="text-sm text-neutral-600 mt-4 border-t border-neutral-100 pt-4">{project.description}</p>}
            {project.cover_brief && <p className="text-sm text-neutral-600 mt-3"><span className="font-medium">Cover brief:</span> {project.cover_brief}</p>}
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-semibold font-heading">Files & versions</h3>
              {isStaff && (
                <div className="flex gap-2">
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50 cursor-pointer">
                    <Upload size={12} /> {uploading === "manuscript" ? "Uploading..." : "Manuscript"}
                    <input data-testid="staff-manuscript-upload" type="file" accept=".docx,.pdf,.epub,.doc" className="hidden" onChange={(e) => staffUpload("manuscript", e)} />
                  </label>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium text-neutral-700 hover:bg-neutral-50 cursor-pointer">
                    <Upload size={12} /> {uploading === "cover" ? "Uploading..." : "Cover"}
                    <input data-testid="staff-cover-upload" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => staffUpload("cover", e)} />
                  </label>
                </div>
              )}
            </div>
            {files.length === 0 ? <p className="text-sm text-neutral-400">No files uploaded yet.</p> : (
              <ul className="space-y-2">
                {files.map((f) => (
                  <li key={f.id} className="flex items-center gap-3 text-sm bg-neutral-50 border border-neutral-200 rounded-md px-4 py-2.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400 w-20">{f.kind}</span>
                    <span className="font-medium text-neutral-800 truncate">{f.filename}</span>
                    <span className="text-xs text-neutral-400">v{f.version}</span>
                    <button data-testid={`download-file-${f.id}`}
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`, "_blank")}
                      className="ml-auto p-1.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md"><Download size={15} /></button>
                    {(isStaff || !project.locked) && (
                      <>
                        <button data-testid={`rename-file-${f.id}`} onClick={() => renameFile(f)} title="Rename file"
                          className="p-1.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md"><Pencil size={14} /></button>
                        <button data-testid={`delete-file-${f.id}`} onClick={() => deleteFile(f)} title="Delete file"
                          className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h3 className="font-semibold font-heading mb-4">Comments</h3>
            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {comments.length === 0 && <p className="text-sm text-neutral-400">No comments yet.</p>}
              {comments.map((c) => (
                <div key={c.id} className={`text-sm rounded-md p-3 border ${c.internal ? "bg-amber-50 border-amber-200" : "bg-neutral-50 border-neutral-200"}`}>
                  <div className="flex justify-between text-xs text-neutral-500 mb-1">
                    <span className="font-medium">{c.author_name}{c.author_role !== "author" ? " (Team)" : ""}{c.internal ? " · internal note" : ""}</span>
                    <span>{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-neutral-800">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                className="flex-1 bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-900" placeholder="Write a message..." />
              <button data-testid="comment-submit" onClick={addComment} className="px-4 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Send</button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {isStaff && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="admin-controls">
              <h3 className="font-semibold font-heading mb-4">Production controls</h3>
              <label className="text-sm font-medium text-neutral-700">Stage</label>
              <select data-testid="status-select" value={statusForm.status} onChange={(e) => setStatusForm((s) => ({ ...s, status: e.target.value }))} className={inputCls}>
                {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
              <label className="text-sm font-medium text-neutral-700 mt-3 block">Internal note (staff only)</label>
              <input data-testid="internal-note-input" value={statusForm.internal_note} onChange={(e) => setStatusForm((s) => ({ ...s, internal_note: e.target.value }))} className={inputCls} />
              <label className="text-sm font-medium text-neutral-700 mt-3 block">Message to author (optional)</label>
              <input data-testid="author-comment-input" value={statusForm.author_comment} onChange={(e) => setStatusForm((s) => ({ ...s, author_comment: e.target.value }))} className={inputCls} />
              <button data-testid="update-status-button" onClick={updateStatus}
                className="mt-4 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Update stage & notify</button>

              <hr className="my-5 border-neutral-100" />
              <button data-testid="generate-packet-button" onClick={downloadPacket}
                className="w-full py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50 inline-flex items-center justify-center gap-2">
                <Package size={15} /> Download Publishing Packet
              </button>

              <hr className="my-5 border-neutral-100" />
              <label className="text-sm font-medium text-neutral-700">ASIN</label>
              <input data-testid="asin-input" value={kdp.asin} onChange={(e) => setKdp((k) => ({ ...k, asin: e.target.value }))} className={inputCls} placeholder="B0XXXXXXXXX" />
              <label className="text-sm font-medium text-neutral-700 mt-3 block">Amazon URL</label>
              <input data-testid="amazon-url-input" value={kdp.amazon_url} onChange={(e) => setKdp((k) => ({ ...k, amazon_url: e.target.value }))} className={inputCls} placeholder="https://amazon.com/dp/..." />
              <button data-testid="save-kdp-button" onClick={saveKdp}
                className="mt-3 w-full py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50">Save listing info</button>

              {project.audiobook && (
                <>
                  <hr className="my-5 border-neutral-100" />
                  <label className="text-sm font-medium text-neutral-700">Audiobook status</label>
                  <select data-testid="audiobook-status-select" value={abForm.status}
                    onChange={(e) => setAbForm((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
                    <option value="requested">Requested</option>
                    <option value="in_production">In production</option>
                    <option value="completed">Completed</option>
                  </select>
                  {project.audiobook.brief && <p className="text-xs text-neutral-500 mt-2">Author notes: {project.audiobook.brief}</p>}
                  <label className="text-sm font-medium text-neutral-700 mt-3 block">Audiobook link (optional)</label>
                  <input data-testid="audiobook-url-input" value={abForm.url}
                    onChange={(e) => setAbForm((f) => ({ ...f, url: e.target.value }))} className={inputCls} placeholder="https://..." />
                  <button data-testid="audiobook-save-button" onClick={saveAudiobook} disabled={abBusy}
                    className="mt-3 w-full py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">Save audiobook & notify</button>
                </>
              )}

              {user.role === "super_admin" && splits.length > 0 && (
                <>
                  <hr className="my-5 border-neutral-100" />
                  <label className="text-sm font-medium text-neutral-700">Royalty split</label>
                  <select data-testid="split-select" value={project.royalty_split?.id || ""} onChange={(e) => setSplit(e.target.value)} className={inputCls}>
                    <option value="" disabled>Choose split</option>
                    {splits.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </>
              )}

              {user.role === "super_admin" && (
                <>
                  <hr className="my-5 border-neutral-100" />
                  <button data-testid="delete-project-button" onClick={deleteProject}
                    className="w-full py-2 border border-red-200 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 inline-flex items-center justify-center gap-2">
                    <Trash2 size={15} /> Delete project
                  </button>
                </>
              )}
            </div>
          )}

          {!isStaff && (project.approvals || []).some((a) => a.status === "pending") && (
            <div className="bg-white border-2 border-amber-300 rounded-lg p-6" data-testid="approval-pending-card">
              <h3 className="font-semibold font-heading mb-1 flex items-center gap-2"><CheckCircle2 size={17} className="text-amber-600" /> Your approval is needed</h3>
              {(project.approvals || []).filter((a) => a.status === "pending").map((a) => (
                <div key={a.id} className="mt-3 border-t border-neutral-100 pt-3 first:border-0 first:pt-0" data-testid={`approval-item-${a.id}`}>
                  <p className="text-sm text-neutral-700">
                    <span className="font-semibold capitalize">{a.kind === "general" ? "Book review" : a.kind}</span> — requested by {a.requested_by} on {new Date(a.requested_at).toLocaleDateString()}
                  </p>
                  {a.message && <p className="text-sm text-neutral-500 mt-1.5 bg-neutral-50 border border-neutral-200 rounded-md p-2.5">{a.message}</p>}
                  <textarea data-testid={`approval-note-${a.id}`} rows={2} value={appNotes[a.id] || ""}
                    onChange={(e) => setAppNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                    className={inputCls + " mt-2"} placeholder="Optional note for our team..." />
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`approval-approve-${a.id}`} onClick={() => respondApproval(a, "approve")} disabled={appBusy}
                      className="flex-1 py-2 rounded-md text-white bg-emerald-600 hover:bg-emerald-700 text-xs font-medium disabled:opacity-50">Approve</button>
                    <button data-testid={`approval-review-${a.id}`} onClick={() => respondApproval(a, "review")} disabled={appBusy}
                      className="flex-1 py-2 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 text-xs font-medium disabled:opacity-50">Request changes</button>
                    <button data-testid={`approval-reject-${a.id}`} onClick={() => respondApproval(a, "reject")} disabled={appBusy}
                      className="flex-1 py-2 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium disabled:opacity-50">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isStaff && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="approvals-admin-card">
              <h3 className="font-semibold font-heading mb-2 flex items-center gap-2"><CheckCircle2 size={17} /> Author approvals</h3>
              {(project.approvals || []).length > 0 && (
                <ul className="space-y-2 mb-4">
                  {(project.approvals || []).slice().reverse().map((a) => (
                    <li key={a.id} className="text-sm bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2.5" data-testid={`approval-status-${a.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="capitalize font-medium">{a.kind}</span>
                        <span className={`ml-auto text-xs font-medium border rounded-full px-2.5 py-0.5 ${
                          a.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : a.status === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                          : a.status === "changes_requested" ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                          {a.status.replace(/_/g, " ")}
                        </span>
                      </div>
                      {a.note && <p className="text-xs text-neutral-500 mt-1.5">Author: “{a.note}”</p>}
                      <p className="text-[11px] text-neutral-400 mt-1">{new Date(a.decided_at || a.requested_at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
              <label className="text-sm font-medium text-neutral-700">Ask the author to review</label>
              <select data-testid="approval-kind-select" value={appForm.kind}
                onChange={(e) => setAppForm((f) => ({ ...f, kind: e.target.value }))} className={inputCls}>
                <option value="manuscript">Manuscript</option>
                <option value="cover">Cover</option>
                <option value="general">Whole book / general</option>
              </select>
              <textarea data-testid="approval-message-input" rows={2} value={appForm.message}
                onChange={(e) => setAppForm((f) => ({ ...f, message: e.target.value }))}
                className={inputCls + " mt-2"} placeholder="Message to the author (what should they check?)" />
              <button data-testid="approval-send-button" onClick={sendApproval} disabled={appBusy}
                className="mt-3 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {appBusy ? "Sending..." : "Request author approval"}
              </button>
            </div>
          )}

          {!isStaff && project.status !== "draft" && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="audiobook-card">
              <h3 className="font-semibold font-heading mb-2 flex items-center gap-2"><Headphones size={17} /> Audiobook</h3>
              {project.audiobook ? (
                <div>
                  <span data-testid="audiobook-status-badge" className={`inline-block text-xs font-medium border rounded-full px-3 py-1 ${
                    project.audiobook.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : project.audiobook.status === "in_production" ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {project.audiobook.status === "completed" ? "Completed" : project.audiobook.status === "in_production" ? "In production" : "Requested"}
                  </span>
                  {project.audiobook.status === "completed" && project.audiobook.url && (
                    <a data-testid="audiobook-link" href={project.audiobook.url} target="_blank" rel="noreferrer"
                      className="block text-sm text-blue-700 hover:underline mt-3">Listen to your audiobook →</a>
                  )}
                  {project.audiobook.status !== "completed" && (
                    <p className="text-sm text-neutral-500 mt-3">Our team is on it — you'll get an email at every update.</p>
                  )}
                </div>
              ) : modules.includes("audiobook") ? (
                <div>
                  <p className="text-sm text-neutral-500 mb-3">Turn this book into a professionally produced audiobook — included with your Premium plan.</p>
                  <textarea data-testid="audiobook-brief-input" rows={3} value={abBrief} onChange={(e) => setAbBrief(e.target.value)}
                    className={inputCls} placeholder="Narration preferences, tone, pronunciation notes... (optional)" />
                  <button data-testid="audiobook-request-button" onClick={requestAudiobook} disabled={abBusy}
                    className="mt-3 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                    {abBusy ? "Requesting..." : "Request audiobook production"}
                  </button>
                </div>
              ) : (
                <div data-testid="audiobook-locked">
                  <p className="text-sm text-neutral-500">Audiobook production is a <span className="font-semibold text-neutral-900">Premium</span> plan feature.</p>
                  <Link to="/profile" className="inline-block mt-3 text-sm font-medium text-blue-700 hover:underline">Upgrade your membership →</Link>
                </div>
              )}
            </div>
          )}

          {!isStaff && project.status !== "draft" && (
            <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="author-copies-card">
              <h3 className="font-semibold font-heading mb-2">Order author copies</h3>
              {Number(project.author_copy_price) > 0 ? (
                <form onSubmit={orderCopies}>
                  <p className="text-sm text-neutral-500 mb-4">
                    Printed copies of your book at your author price of <span className="font-semibold text-neutral-900">${Number(project.author_copy_price).toFixed(2)}</span>/copy — taxes & shipping included.
                  </p>
                  <label className="text-sm font-medium text-neutral-700">Quantity</label>
                  <input data-testid="author-copies-qty-input" type="number" min="1" max="500" value={copyQty}
                    onChange={(e) => setCopyQty(e.target.value)} className={inputCls} />
                  <p className="text-sm font-semibold text-neutral-700 mt-4 mb-1">Ship to</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="copies-ship-name" required placeholder="Full name" value={copyShip.name}
                      onChange={(e) => setCopyShip((s) => ({ ...s, name: e.target.value }))} className={inputCls} />
                    <input data-testid="copies-ship-email" type="email" required placeholder="Email" value={copyShip.email}
                      onChange={(e) => setCopyShip((s) => ({ ...s, email: e.target.value }))} className={inputCls} />
                    <input data-testid="copies-ship-address1" required placeholder="Address" value={copyShip.address1}
                      onChange={(e) => setCopyShip((s) => ({ ...s, address1: e.target.value }))} className={inputCls + " col-span-2"} />
                    <input data-testid="copies-ship-city" required placeholder="City" value={copyShip.city}
                      onChange={(e) => setCopyShip((s) => ({ ...s, city: e.target.value }))} className={inputCls} />
                    <div className="grid grid-cols-2 gap-2">
                      <input placeholder="State" value={copyShip.state}
                        onChange={(e) => setCopyShip((s) => ({ ...s, state: e.target.value }))} className={inputCls} />
                      <input data-testid="copies-ship-zip" required placeholder="ZIP" value={copyShip.postal_code}
                        onChange={(e) => setCopyShip((s) => ({ ...s, postal_code: e.target.value }))} className={inputCls} />
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mt-3">
                    Total: <span className="font-bold text-neutral-900">${(Number(project.author_copy_price) * (parseInt(copyQty) || 0)).toFixed(2)}</span>
                  </p>
                  <button data-testid="author-copies-order-button" disabled={ordering || !(parseInt(copyQty) > 0)}
                    className="mt-4 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                    {ordering ? "Redirecting to checkout..." : "Order & pay"}
                  </button>
                </form>
              ) : (
                <p className="text-sm text-neutral-500" data-testid="author-copies-pending">
                  Author copy pricing for this book hasn't been set yet — message our team below and we'll enable ordering.
                </p>
              )}
            </div>
          )}

          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <h3 className="font-semibold font-heading mb-4">Status history</h3>
            {(project.status_history || []).length === 0 ? <p className="text-sm text-neutral-400">No changes yet.</p> : (
              <ol className="space-y-3">
                {[...project.status_history].reverse().map((h, i) => (
                  <li key={i} className="text-sm border-l-2 border-neutral-200 pl-3">
                    <p className="font-medium text-neutral-800">{STAGE_LABELS[h.status] || h.status}</p>
                    <p className="text-xs text-neutral-400">{new Date(h.at).toLocaleString()} · {h.changed_by}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
